import { useEffect, useMemo, useRef, useState, Suspense } from "react";
import axios from "axios";
import { Canvas } from "@react-three/fiber";
import { XR, createXRStore, XROrigin } from "@react-three/xr";
import GlobeScene from "./three/GlobeScene";
import CityScene from "./three/CityScene";
import VRControls from "./three/VRControls";
import Hud from "./components/Hud";
import Landing from "./components/Landing";
import { NODES, CITIES, lerpTrack, topByWind } from "./lib/storm";
import { CYCLES, resolvePreset } from "./lib/environment";
import { getProfile } from "./lib/quality";
import AudioEngine from "./lib/audio";
import "./App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function App() {
  const store = useMemo(() => createXRStore({
    // Disable the IWER WebXR emulator dev-tooling mount. It dynamically imports
    // @iwer/devui, which bundles three@0.165.0 and causes a "multiple instances of
    // Three.js" runtime error. Native WebXR (enterVR) still works on Quest & Vision Pro.
    emulate: false,
    hand: { teleportPointer: true },
    controller: { teleportPointer: true },
  }), []);
  const simRef = useRef({ intensity: 3 });
  const moveRef = useRef({ f: false, b: false, l: false, r: false, tl: false, tr: false });
  const originRef = useRef();
  const audio = useMemo(() => new AudioEngine(), []);
  const audioRef = useRef(null);
  const chaseTimer = useRef(null);

  const [stage, setStage] = useState("splash");
  const [view, setView] = useState("globe");
  const [intensity, setIntensity] = useState(3);
  const [timeline, setTimeline] = useState(0.4);
  const [autoMode, setAutoMode] = useState(false);
  const [cycle, setCycle] = useState("hurricane");
  const [timeOfDay, setTimeOfDay] = useState("afternoon");
  const [autoSolar, setAutoSolar] = useState(null);
  const [cityCam, setCityCam] = useState("walk");
  const [viewMode, setViewMode] = useState("realistic");
  const [xrPos, setXrPos] = useState([0, 0, 0]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [events, setEvents] = useState([]);
  const [liveMetrics, setLiveMetrics] = useState(null);
  const [blizzardMetrics, setBlizzardMetrics] = useState(null);
  const [narrator, setNarrator] = useState({ loading: false, playing: false, text: "" });
  const [chasePlaying, setChasePlaying] = useState(false);
  const [a11y, setA11y] = useState({ reducedMotion: false, mute: false });
  const [audioUI, setAudioUI] = useState({ chaos: 0.7, voice: 1.0, mute: false });
  const [isPortrait, setIsPortrait] = useState(false);
  const [viewport, setViewport] = useState({ w: typeof window !== "undefined" ? window.innerWidth : 1920, h: typeof window !== "undefined" ? window.innerHeight : 1080 });
  const [flash, setFlash] = useState(false);
  const [autoFollow, setAutoFollow] = useState(false);
  const [focusLatLon, setFocusLatLon] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [liveFeatured, setLiveFeatured] = useState({});
  const [xrSupported, setXrSupported] = useState(false);
  const transitioningRef = useRef(false);

  useEffect(() => {
    const on = () => { setIsPortrait(window.innerHeight > window.innerWidth); setViewport({ w: window.innerWidth, h: window.innerHeight }); };
    on(); window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  // Detect native WebXR immersive-vr support (Meta Quest / Apple Vision Pro browsers)
  useEffect(() => {
    let alive = true;
    if (typeof navigator !== "undefined" && navigator.xr?.isSessionSupported) {
      navigator.xr.isSessionSupported("immersive-vr").then((ok) => { if (alive) setXrSupported(!!ok); }).catch(() => {});
    }
    return () => { alive = false; };
  }, []);

  const cycleCfg = CYCLES[cycle];
  const activeNode = cycleCfg.node;
  const preset = useMemo(() => resolvePreset(timeOfDay, autoSolar), [timeOfDay, autoSolar]);

  const isMobile = useMemo(() => (typeof window !== "undefined" && (window.innerWidth < 768 || "ontouchstart" in window)), []);
  const baseProfile = useMemo(() => getProfile({ isMobile, isVR: false }), [isMobile]);

  useEffect(() => { simRef.current.intensity = intensity; audio.setIntensity(intensity / 5); }, [intensity, audio]);
  useEffect(() => { audio.setNight(!!preset.night); }, [preset, audio]);

  // splash -> landing after 2s
  useEffect(() => { const t = setTimeout(() => setStage((s) => (s === "splash" ? "landing" : s)), 2200); return () => clearTimeout(t); }, []);

  // audio mute sync
  const effMute = audioUI.mute || a11y.mute;
  useEffect(() => { audio.setMuted(effMute); }, [effMute, audio]);
  useEffect(() => { audio.setChaosVolume(audioUI.chaos); }, [audioUI.chaos, audio]);
  useEffect(() => { audio.setVoiceVolume(audioUI.voice); }, [audioUI.voice, audio]);

  useEffect(() => { axios.get(`${API}/archive/events`).then((r) => setEvents(r.data)).catch(() => {}); }, []);

  // Live metrics for the two current featured systems (auto-refresh every minute)
  useEffect(() => {
    let alive = true;
    const pull = () => Promise.all([
      axios.get(`${API}/storm/live/hurricane_polo`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/storm/live/noreaster`).then((r) => r.data).catch(() => null),
    ]).then(([h, n]) => { if (alive) setLiveFeatured((prev) => ({ hurricane_polo: h || prev.hurricane_polo, noreaster: n || prev.noreaster })); });
    pull();
    const id = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let alive = true;
    const f = () => axios.get(`${API}/storm/live/${activeNode}`).then((r) => { if (alive) setLiveMetrics(r.data); }).catch(() => {});
    f(); const id = setInterval(f, 60000); return () => { alive = false; clearInterval(id); };
  }, [activeNode]);

  useEffect(() => {
    if (!autoMode) return;
    let alive = true;
    const pull = () => axios.get(`${API}/storm/auto-intensity/${activeNode}`).then((r) => { if (alive) setIntensity(r.data.level); }).catch(() => {});
    pull(); const id = setInterval(pull, 30000); return () => { alive = false; clearInterval(id); };
  }, [autoMode, activeNode]);

  useEffect(() => {
    if (timeOfDay !== "auto") return;
    let alive = true;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const go = () => { const d = new Date(); axios.post(`${API}/time/auto-solar`, { hour: d.getHours(), minute: d.getMinutes(), tz }).then((r) => { if (alive) setAutoSolar(r.data); }).catch(() => {}); };
    go(); const id = setInterval(go, 600000); return () => { alive = false; clearInterval(id); };
  }, [timeOfDay]);

  useEffect(() => {
    if (cycle !== "blizzard") return;
    let alive = true;
    axios.get(`${API}/storm/blizzard`).then((r) => { if (alive) setBlizzardMetrics(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [cycle]);

  const activeTrack = selectedEvent ? selectedEvent.track : NODES[activeNode].track;
  // Intensity ramp: storm strengthens toward landfall / as the eye passes the coast (Cat 5 near the end)
  useEffect(() => {
    if (autoMode) return;
    const ramp = Math.min(5, (timeline / 0.8) * 5);
    setIntensity(Math.round(ramp * 100) / 100);
  }, [timeline, autoMode]);
  const stormPos = useMemo(() => ({
    hurricane_polo: lerpTrack(activeNode === "hurricane_polo" ? activeTrack : NODES.hurricane_polo.track, timeline),
    noreaster: lerpTrack(activeNode === "noreaster" ? activeTrack : NODES.noreaster.track, timeline),
  }), [activeNode, activeTrack, timeline]);

  const onSelectEvent = (ev) => {
    setSelectedEvent(ev); setIntensity(ev.peak_level); setAutoMode(false);
    const t = ev.type.toLowerCase();
    if (t.includes("blizzard") || ev.id === "blizzard1888") setCycle("blizzard");
    else if (t.includes("cold") || t.includes("nor")) setCycle("noreaster");
    else setCycle("hurricane");
  };

  const precipSnow = cycleCfg.precipSnow || (selectedEvent && selectedEvent.id === "blizzard1888");

  // The two current systems the report tracks — shown as the landing "Featured" cards
  // Center the globe but auto-size it so it keeps a >=50px corridor from the far-left
  // console (32+250) and the far-right menu, staying large & profound on wide screens.
  const landingGlobeScale = useMemo(() => {
    if (isPortrait) return 1.15;
    const fov = 55, d = 3.4, f = 0.901; // camera dist + effective (radius*atmosphere)
    const ppw = (viewport.h / 2) / (d * Math.tan((fov / 2) * Math.PI / 180));
    const corridorHalf = viewport.w / 2 - (32 + 250 + 50);
    return Math.max(1.0, Math.min(1.6, corridorHalf / (f * ppw)));
  }, [isPortrait, viewport]);

  const featuredSystems = useMemo(() => (
    [NODES.hurricane_polo, NODES.noreaster].map((n) => {
      const cityId = n.id === "hurricane_polo" ? "acapulco" : "nyc";
      return {
        ...n,
        live: liveFeatured[n.id] || null,
        landfallCity: CITIES.find((c) => c.id === cityId) || null,
        sstFallback: n.id === "hurricane_polo" ? 29.4 : 15.5,
      };
    })
  ), [liveFeatured]);
  const onSelectSystem = (n) => {
    setSelectedEvent(null);
    setCycle(n.id === "hurricane_polo" ? "hurricane" : "noreaster");
    setAutoFollow(true);
  };

  const cloudPierce = (apply) => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;
    setFlash(true);
    setTimeout(apply, 480);
    setTimeout(() => { setFlash(false); transitioningRef.current = false; }, 1000);
  };

  const onDiveCity = (city) => {
    audio.start(); audio.setIntensity(intensity / 5);
    // Select City Sync: orbit to the associated storm's coordinates before diving
    const pacific = city.id === "acapulco" || (city.lon < -95 && Math.abs(city.lat) < 25);
    const storm = pacific ? stormPos.hurricane_polo : stormPos.noreaster;
    const dive = () => cloudPierce(() => { setSelectedCity(city); setXrPos([0, 0, 0]); setView("city"); setStage("immersive"); setFocusLatLon(null); });
    if (storm && view === "globe") { setFocusLatLon(storm); setTimeout(dive, 900); }
    else dive();
  };
  const onZoomDive = onDiveCity;
  // Featured card shortcut: pick the system, then dive straight into its landfall city
  const onDiveSystem = (n) => {
    setSelectedEvent(null);
    setCycle(n.id === "hurricane_polo" ? "hurricane" : "noreaster");
    const city = n.landfallCity || CITIES.find((c) => c.id === (n.id === "hurricane_polo" ? "acapulco" : "nyc"));
    if (city) onDiveCity(city);
  };
  const onReturnGlobe = () => { setView("globe"); if (audioRef.current) audioRef.current.pause(); setNarrator((n) => ({ ...n, playing: false })); };

  const enterImmersive = () => { audio.start(); audio.setIntensity(intensity / 5); cloudPierce(() => setStage("immersive")); };
  // Landing shortcut for headset users: go immersive and launch the WebXR session directly
  const onEnterVR = async () => {
    audio.start(); audio.setIntensity(intensity / 5);
    setStage("immersive");
    try {
      const r = store.enterVR();
      if (r && typeof r.then === "function") await r;
    } catch (e) {
      console.error("enterVR failed", e);
      alert("Couldn't start the VR session. Open this page in a WebXR browser on Meta Quest 3 or Apple Vision Pro, then tap Enter VR.");
    }
  };
  const featuredEvent = useMemo(() => {
    if (!events.length) return null;
    const top = topByWind(events, 5);
    return top[Math.floor(Math.random() * top.length)];
  }, [events]);

  // Global click-to-dive: any landmass -> nearest city anchor, or a procedural coastal site
  const onDiveLocation = (lat, lon) => {
    let best = null, bd = 1e9;
    for (const c of CITIES) { const d = Math.hypot(c.lat - lat, c.lon - lon); if (d < bd) { bd = d; best = c; } }
    if (best && bd < 6) { onDiveCity(best); return; }
    const climate = Math.abs(lat) < 30 ? "tropical" : "temperate";
    const ns = lat >= 0 ? "N" : "S", ew = lon >= 0 ? "E" : "W";
    const site = { id: `site_${lat.toFixed(0)}_${lon.toFixed(0)}`, name: `Coastal Site ${Math.abs(lat).toFixed(1)}°${ns} ${Math.abs(lon).toFixed(1)}°${ew}`, lat, lon, climate, custom: true };
    onDiveCity(site);
  };

  // Storm scenarios (named, persisted in MongoDB)
  const fetchScenarios = () => axios.get(`${API}/scenarios`).then((r) => setScenarios(r.data)).catch(() => {});
  useEffect(() => { fetchScenarios(); }, []);
  const onSaveScenario = async (name) => {
    if (!name) return;
    await axios.post(`${API}/scenarios`, { name, cycle, intensity, time_of_day: timeOfDay, view_mode: viewMode, event_id: selectedEvent?.id || null }).catch(() => {});
    fetchScenarios();
  };
  const onLoadScenario = (s) => {
    setCycle(s.cycle); setIntensity(s.intensity); setTimeOfDay(s.time_of_day); setViewMode(s.view_mode);
    if (s.event_id) { const ev = events.find((e) => e.id === s.event_id); if (ev) onSelectEvent(ev); }
    else setSelectedEvent(null);
  };
  const onDeleteScenario = async (id) => { await axios.delete(`${API}/scenarios/${id}`).catch(() => {}); fetchScenarios(); };

  const playNarratorText = (text, audioUrl) => {
    if (!audioUrl) return;
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}${audioUrl}`);
    a.crossOrigin = "anonymous";
    audioRef.current = a;
    audio.attachVoice(a);
    a.onended = () => setNarrator((n) => ({ ...n, playing: false }));
    a.play().catch(() => setNarrator((n) => ({ ...n, playing: false })));
  };

  const onPlayNarrator = async () => {
    setNarrator({ loading: true, playing: false, text: "" });
    try {
      const { data } = await axios.post(`${API}/narrator/tts`, { node: activeNode, city: selectedCity?.name || null, intensity: Math.round(intensity), voice: "onyx" });
      setNarrator({ loading: false, playing: !!data.audio_url, text: data.text });
      playNarratorText(data.text, data.audio_url);
    } catch (e) { setNarrator({ loading: false, playing: false, text: "Situation report unavailable." }); }
  };

  // Storm Chase Playback: fly the storm along its track with narration
  const onChase = () => {
    if (chasePlaying) { setChasePlaying(false); if (chaseTimer.current) clearInterval(chaseTimer.current); return; }
    setChasePlaying(true);
    onPlayNarrator();
    let t = 0;
    setTimeline(0);
    chaseTimer.current = setInterval(() => {
      t += 0.006;
      if (t >= 1) { t = 1; clearInterval(chaseTimer.current); setChasePlaying(false); }
      setTimeline(t);
    }, 100);
  };
  useEffect(() => () => { if (chaseTimer.current) clearInterval(chaseTimer.current); }, []);

  return (
    <div className="App void-bg">
      <Canvas
        dpr={[1, baseProfile.dpr]}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        camera={{ position: [0, 1.2, 1.75], fov: 55, near: 0.05, far: 200 }}
        data-testid="xr-canvas"
      >
        <color attach="background" args={["#02030a"]} />
        <XR store={store}>
          <XROrigin ref={originRef} position={xrPos} />
          <VRControls sim={simRef} originRef={originRef} timeLabel={preset.label} audio={{ mute: effMute, chaos: audioUI.chaos }} />
          <Suspense fallback={null}>
            {view === "globe" ? (
              <GlobeScene sim={simRef} activeNode={activeNode} stormPos={stormPos}
                eventTrack={selectedEvent ? selectedEvent.track : null} event={selectedEvent} onSelectCity={onDiveCity} preset={preset} cycle={cycleCfg} viewMode={viewMode}
                framing={stage === "immersive" ? "immersive" : "landing"}
                isPortrait={isPortrait}
                globeScale={stage === "immersive" ? 1 : landingGlobeScale}
                enableDive={view === "globe"} onZoomDive={onZoomDive}
                onDiveLocation={onDiveLocation} autoFollow={autoFollow} followLatLon={stormPos[activeNode]} focusLatLon={focusLatLon}
                timeline={timeline} profile={baseProfile}
                reducedMotion={a11y.reducedMotion} />
            ) : (
              <CityScene sim={simRef} city={selectedCity} event={selectedEvent} precipSnow={precipSnow}
                moveRef={moveRef} preset={preset} cycle={cycleCfg} cityCam={cityCam} onTeleport={setXrPos}
                profile={baseProfile} timeline={timeline} reducedMotion={a11y.reducedMotion} />
            )}
          </Suspense>
        </XR>
      </Canvas>

      {stage !== "immersive" ? (
        <Landing stage={stage} onEnter={enterImmersive} a11y={a11y} setA11y={setA11y}
          intensity={intensity} setIntensity={setIntensity}
          featured={featuredEvent} onSelectEvent={onSelectEvent}
          featuredSystems={featuredSystems} onSelectSystem={onSelectSystem} onDiveSystem={onDiveSystem}
          events={events}
          xrSupported={xrSupported} onEnterVR={onEnterVR}
          timeOfDay={timeOfDay} setTimeOfDay={setTimeOfDay} />
      ) : (
        <Hud
          view={view}
          intensity={intensity} setIntensity={setIntensity}
          timeline={timeline} setTimeline={setTimeline}
          autoMode={autoMode} setAutoMode={setAutoMode}
          cycle={cycle} setCycle={setCycle}
          viewMode={viewMode} setViewMode={setViewMode}
          timeOfDay={timeOfDay} setTimeOfDay={setTimeOfDay} autoSolar={autoSolar}
          cityCam={cityCam} setCityCam={setCityCam}
          activeNode={activeNode} liveMetrics={liveMetrics} blizzardMetrics={blizzardMetrics}
          events={events} selectedEvent={selectedEvent} onSelectEvent={onSelectEvent}
          onDiveCity={onDiveCity} selectedCity={selectedCity} onReturnGlobe={onReturnGlobe}
          onPlayNarrator={onPlayNarrator} narrator={narrator}
          onChase={onChase} chasePlaying={chasePlaying}
          autoFollow={autoFollow} setAutoFollow={setAutoFollow}
          scenarios={scenarios} onSaveScenario={onSaveScenario} onLoadScenario={onLoadScenario} onDeleteScenario={onDeleteScenario}
          audioUI={audioUI} setAudioUI={setAudioUI} a11y={a11y} setA11y={setA11y}
          moveRef={moveRef} xrStore={store} xrSupported={xrSupported}
        />
      )}
      <div className={`cloud-pierce ${flash ? "on" : ""}`} data-testid="cloud-pierce" />
    </div>
  );
}

export default App;
